import { Controller } from '@hotwired/stimulus';

declare const $: any;
declare const chrono: any;

export default class extends Controller {
  private static readonly NATURAL_LANGUAGE_DEBOUNCE_MS = 400;
  private static readonly MINUTE_STEPS = [0, 15, 30, 45, 59];
  private static readonly DEFAULT_HOUR_12 = '10';
  private static readonly DEFAULT_HOUR_24 = '10';
  private static readonly DEFAULT_MINUTE = '15';
  private static readonly DEFAULT_AMPM = 'AM';
  static targets = ["display", "hidden", "calendar", "button", "clear", "full"];
  private static readonly MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  private static readonly MONTH_NAMES_ABBR = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  declare displayTarget: HTMLInputElement;
  declare hiddenTarget: HTMLInputElement;
  declare calendarTarget: HTMLInputElement;
  declare buttonTarget: HTMLElement;
  declare clearTarget: HTMLButtonElement;
  declare fullTarget: HTMLInputElement;
  declare hasDisplayTarget: boolean;
  declare hasHiddenTarget: boolean;
  declare hasCalendarTarget: boolean;
  declare hasClearTarget: boolean;
  declare hasFullTarget: boolean;

  private clearingClickInProgress: boolean = false;
  private naturalLanguagePreviewTimeout?: number;

  private timeContainer?: HTMLElement;
  private hourSelect?: HTMLSelectElement;
  private minuteSelect?: HTMLSelectElement;
  private ampmSelect?: HTMLSelectElement;

  connect() {
    this.initializeWithRetry();
  }

  private initializeWithRetry(attempt: number = 0): void {
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 200;

    if (typeof $ === 'undefined' || typeof $.fn.pickadate !== 'function') {
      if (attempt < MAX_RETRIES) {
        window.setTimeout(() => this.initializeWithRetry(attempt + 1), RETRY_DELAY_MS);
        return;
      }
      console.warn('[date-picker] dependencies not available after retries, using legacy fallback');
      this.initializeLegacyFallback();
      return;
    }

    try {
      if (this.hasCalendarTarget) this.initializeCalendar();
      if (this.hasFullTarget) {
        this.syncInitialValue();
        this.injectTimeUIOnOpen();
        this.setupEventListeners();
        this.toggleClearVisibility();
      } else {
        this.initializeLegacyFallback();
      }
    } catch (e) {
      console.error('[date-picker] initialization error', e);
      this.initializeLegacyFallback();
    }
  }

  disconnect() {
    if (this.naturalLanguagePreviewTimeout) {
      window.clearTimeout(this.naturalLanguagePreviewTimeout);
    }
  }

  private setupEventListeners(): void {
    this.fullTarget.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.open();
      }
    });

    this.fullTarget.addEventListener('input', () => {
      this.toggleClearVisibility();
    });

    this.fullTarget.addEventListener('keyup', () => {
      if (this.naturalLanguagePreviewTimeout) {
        window.clearTimeout(this.naturalLanguagePreviewTimeout);
      }
      this.naturalLanguagePreviewTimeout = window.setTimeout(() => {
        this.handleNaturalLanguagePreview();
      }, (this.constructor as any).NATURAL_LANGUAGE_DEBOUNCE_MS);
    });

    this.fullTarget.addEventListener('blur', () => {
      if (this.clearingClickInProgress) return;

      if (this.naturalLanguagePreviewTimeout) {
        window.clearTimeout(this.naturalLanguagePreviewTimeout);
      }

      this.applyNaturalLanguageParsing();
      this.hideTooltip();
      this.toggleClearVisibility();
    });

    this.fullTarget.addEventListener('focus', () => {
      this.showTooltip();
      this.toggleClearVisibility();
    });

    if (this.hasClearTarget) {
      this.clearTarget.addEventListener('mousedown', () => {
        this.clearingClickInProgress = true;
      });
      this.clearTarget.addEventListener('click', (e) => this.clear(e));
    }
  }

  private ensureTooltipExists(): void {
    let tooltip = this.element.querySelector('.date-picker-hint') as HTMLElement;
    if (!tooltip) {
      tooltip = document.createElement('span');
      tooltip.className = 'date-picker-hint toolkit-date-picker__hint';
      tooltip.textContent = tooltip.dataset.text || '';

      const fieldGroup = this.element.querySelector('.toolkit-date-picker__field-group');
      const container = fieldGroup || this.element;
      container.appendChild(tooltip);
    }
  }

  private showTooltip(): void {
    const tooltip = this.element.querySelector('.date-picker-hint') as HTMLElement;
    if (!tooltip) return;

    tooltip.style.display = 'block';

    const inputValue = this.fullTarget.value.trim();
    if (inputValue) {
      this.handleNaturalLanguagePreview();
    } else {
      this.restoreTooltipText();
    }
  }

  private hideTooltip(): void {
    const tooltip = this.element.querySelector('.date-picker-hint') as HTMLElement;
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  }

  private restoreTooltipText(): void {
    const tooltip = this.element.querySelector('.date-picker-hint') as HTMLElement;
    if (tooltip?.dataset.text) {
      tooltip.textContent = tooltip.dataset.text;
    }
  }

  clear(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.hasDisplayTarget) return;

    this.displayTarget.value = '';
    if (this.hasHiddenTarget) this.hiddenTarget.value = '';

    const picker = this.picker();
    try {
      picker?.clear();
    } catch {
      // Ignore picker errors
    }

    this.restoreTooltipText();
    this.hideTooltip();

    this.displayTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.clearingClickInProgress = false;
    this.toggleClearVisibility();
  }

  toggleClearVisibility(): void {
    if (!this.hasClearTarget) return;

    const hasValue = !!this.displayTarget?.value?.trim();
    const focused = document.activeElement === this.displayTarget;

    if (!hasValue && focused) {
      this.restoreTooltipText();
    }

    this.clearTarget.style.display = (hasValue && focused) ? 'inline-flex' : 'none';
  }

  open(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    let picker = this.picker();
    if (!picker) {
      this.initializeCalendar();
      picker = this.picker();
    }
    picker?.open();
  }

  initializeCalendar(): void {
    if (!this.calendarTarget || typeof $ === 'undefined') return;
    if ($(this.calendarTarget).data('hasPickadate')) return;

    const min = this.fullTarget?.dataset.min;
    const max = this.fullTarget?.dataset.max;

    const options: Record<string, unknown> = {
      clear: '',
      closeOnSelect: false,
      onClose: () => this.handleOnCalendarClose(),
      onSet: (context: any) => this.handleOnDateSet(context)
    };

    if (min) options['min'] = new Date(min);
    if (max) options['max'] = new Date(max);

    $(this.calendarTarget).pickadate(options);
    $(this.calendarTarget).data('hasPickadate', true);
  }

  initializeLegacyFallback(): void {
    if (typeof $ === 'undefined') return;

    const $inputs = $(this.element).find('.datepicker_input');
    $inputs.each(function(this: any) {
      if ($(this).data('hasPickadate')) return;

      try {
        $(this).pickadate({ clear: '' });
        $(this).data('hasPickadate', true);
      } catch (e) {
        console.warn('[date-picker] legacy initialization failed', e);
      }
    });
  }

  injectTimeUIOnOpen(): void {
    const picker = this.picker();
    if (!picker) {
      console.warn('[date-picker] picker missing, cannot bind events');
      return;
    }

    picker.on('open', () => {
      this.tryInjectTimeUI(picker, 'open-event');
    });

    picker.on('render', () => {
      window.requestAnimationFrame(() => {
        this.tryInjectTimeUI(picker, 'render-event');
      });
    });
  }

  private tryInjectTimeUI(picker: any, phase: string): void {
    const $root = picker.$root || $(this.calendarTarget).closest('.picker');
    if (!$root.length) return;

    const $box = $root.find('.picker__box');
    if (!$box.length) return;

    if (this.timeContainer && $box[0].contains(this.timeContainer)) {
      return;
    }

    if (!this.timeContainer) {
      this.buildTimeUI();
    }

    const $footer = $root.find('.picker__footer');

    if ($footer.length) {
      this.timeContainer!.classList.add('toolkit-date-picker__time--in-popup');
      $footer.before(this.timeContainer!);
    } else {
      $box.append(this.timeContainer!);
    }

    this.syncTimeSelectsFromFull();
    this.bindTimeEvents();
  }

  private resetTimeRefs(): void {
    this.timeContainer = undefined;
    this.hourSelect = undefined;
    this.minuteSelect = undefined;
    this.ampmSelect = undefined;
  }

  bindTimeEvents(): void {
    [this.hourSelect, this.minuteSelect, this.ampmSelect].forEach(sel => {
      sel?.addEventListener('change', () => this.updateFullFromSelects());
    });
  }

  is24HourFormat(): boolean {
    const timeFormat = this.element.getAttribute('data-time-format');
    return timeFormat === 'HH:mm';
  }

  syncInitialValue(): void {
    let date: Date | null = null;

    if (this.displayTarget?.value) {
      date = this.parseDateTimeString(this.displayTarget.value);
    }

    if (!date && this.hasHiddenTarget && this.hiddenTarget.value) {
      const dateOnlyMatch = this.hiddenTarget.value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const agnosticMatch = this.hiddenTarget.value.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
      if (dateOnlyMatch) {
        const [, year, month, day] = dateOnlyMatch;
        // Build local date explicitly to avoid UTC parsing shifts from new Date('YYYY-MM-DD').
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else if (agnosticMatch) {
        const [, year, month, day, hours, minutes, seconds] = agnosticMatch;
        date = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hours),
          parseInt(minutes),
          parseInt(seconds)
        );
      } else {
        date = new Date(this.hiddenTarget.value);
      }
      if (isNaN(date.getTime())) date = null;
    }

    if (date && !isNaN(date.getTime())) {
      this.picker()?.set('select', date, { muted: true });

      if (!this.displayTarget.value) {
        this.displayTarget.value = this.formatFullDateTime(date);
      }

      if (this.hasHiddenTarget) {
        this.hiddenTarget.value = this.formatDateTimeForRails(date);
      }
    }

    this.syncTimeSelectsFromFull();
  }

  syncTimeSelectsFromFull(): void {
    if (!this.timeContainer) return;

    const raw = this.displayTarget?.value;
    if (!raw) return;

    const date = this.parseDateTimeString(raw);
    if (!date || isNaN(date.getTime())) return;

    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (this.is24HourFormat()) {
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      if (this.hourSelect) this.hourSelect.value = hh;
      if (this.minuteSelect) this.minuteSelect.value = mm;
    } else {
      const hour12 = this.toHour12(hours);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const mm = String(minutes).padStart(2, '0');
      if (this.hourSelect) this.hourSelect.value = hour12;
      if (this.minuteSelect) this.minuteSelect.value = mm;
      if (this.ampmSelect) this.ampmSelect.value = ampm;
    }
  }

  private syncTimeSelectsFromParsedDate(date: Date): void {
    if (!this.timeContainer) return;

    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (this.is24HourFormat()) {
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      if (this.hourSelect) this.hourSelect.value = hh;
      if (this.minuteSelect) this.minuteSelect.value = mm;
    } else {
      const hour12 = this.toHour12(hours);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const mm = String(minutes).padStart(2, '0');
      if (this.hourSelect) this.hourSelect.value = hour12;
      if (this.minuteSelect) this.minuteSelect.value = mm;
      if (this.ampmSelect) this.ampmSelect.value = ampm;
    }
  }

  handleOnCalendarClose(): void {
    const picker = this.picker();
    const selected = picker?.get('select');
    const dateObj = selected ? new Date(selected.obj || selected.pick) : null;
    if (!dateObj || isNaN(dateObj.getTime())) return;

    const existingDate = this.parseDateTimeString(this.displayTarget.value);
    if (existingDate && !isNaN(existingDate.getTime())) {
      dateObj.setHours(existingDate.getHours(), existingDate.getMinutes(), 0, 0);
    } else {
      if (this.hourSelect && !this.hourSelect.value) {
        this.hourSelect.value = this.defaultHour();
      }
      if (this.minuteSelect && !this.minuteSelect.value) {
        this.minuteSelect.value = this.defaultMinute();
      }
      if (!this.is24HourFormat() && this.ampmSelect && !this.ampmSelect.value) {
        this.ampmSelect.value = this.defaultAmpm();
      }
    }
    this.updateFull(dateObj);
  }

  handleOnDateSet(context: any): void {
    let dateObj: Date | null = null;

    if (context?.select) {
      dateObj = new Date(context.select);
    } else {
      const picker = this.picker();
      const selected = picker?.get('select');
      if (selected) dateObj = new Date(selected.obj || selected.pick);
    }

    if (!dateObj || isNaN(dateObj.getTime())) return;

    const existingDate = this.parseDateTimeString(this.displayTarget.value);
    if (existingDate && !isNaN(existingDate.getTime())) {
      dateObj.setHours(existingDate.getHours(), existingDate.getMinutes(), 0, 0);
    } else {
      if (this.hourSelect && !this.hourSelect.value) {
        this.hourSelect.value = this.defaultHour();
      }
      if (this.minuteSelect && !this.minuteSelect.value) {
        this.minuteSelect.value = this.defaultMinute();
      }
      if (!this.is24HourFormat() && this.ampmSelect && !this.ampmSelect.value) {
        this.ampmSelect.value = this.defaultAmpm();
      }
    }
    this.updateFull(dateObj, { silent: true });
  }

  updateFullFromSelects(): void {
    let baseDate: Date;
    const picker = this.picker();
    const selected = picker?.get('select');

    if (selected) {
      baseDate = new Date(selected.obj || selected.pick);
    } else {
      const existingDate = this.parseDateTimeString(this.displayTarget.value);
      baseDate = existingDate || new Date();
    }

    const hourValue = this.hourSelect?.value || this.defaultHour();
    const minuteValue = this.minuteSelect?.value || this.defaultMinute();

    const hourNum = this.is24HourFormat()
      ? parseInt(hourValue, 10)
      : this.convertTo24Hour(parseInt(hourValue, 10), this.ampmSelect?.value || this.defaultAmpm());

    baseDate.setHours(hourNum, parseInt(minuteValue, 10), 0, 0);

    const datePart = this.formatDate(baseDate);
    let displayValue: string;
    if (this.is24HourFormat()) {
      displayValue = `${datePart} ${hourValue}:${minuteValue}`;
    } else {
      const ampm = this.ampmSelect?.value || this.defaultAmpm();
      displayValue = `${datePart} ${hourValue}:${minuteValue} ${ampm}`;
    }

    this.displayTarget.value = displayValue;

    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = this.formatDateTimeForRails(baseDate);
    }

    this.displayTarget.dispatchEvent(new Event('change', { bubbles: true }));
    requestAnimationFrame(() => this.toggleClearVisibility());
  }

  updateFull(date: Date, options: { silent?: boolean } = {}): void {
    const datePart = this.formatDate(date);

    let hour: string;
    let minute: string;
    let ampm: string | undefined;

    if (this.hourSelect?.value && this.minuteSelect?.value) {
      hour = this.hourSelect.value;
      minute = this.minuteSelect.value;
      if (!this.is24HourFormat()) {
        ampm = this.ampmSelect?.value || this.defaultAmpm();
      }
    } else {
      const hours = date.getHours();
      const minutes = date.getMinutes();

      if (this.is24HourFormat()) {
        hour = String(hours).padStart(2, '0');
        minute = String(minutes).padStart(2, '0');
      } else {
        hour = this.toHour12(hours);
        minute = String(minutes).padStart(2, '0');
        ampm = hours >= 12 ? 'PM' : 'AM';
      }
    }
    let displayValue: string;
    if (this.is24HourFormat()) {
      displayValue = `${datePart} ${hour}:${minute}`;
    } else {
      displayValue = `${datePart} ${hour}:${minute} ${ampm}`;
    }

    this.displayTarget.value = displayValue;

    if (this.hasHiddenTarget) {
      const fullDate = new Date(date);
      const hourNum = this.is24HourFormat()
        ? parseInt(hour, 10)
        : this.convertTo24Hour(parseInt(hour, 10), ampm || this.defaultAmpm());
      fullDate.setHours(hourNum, parseInt(minute, 10), 0, 0);
      this.hiddenTarget.value = this.formatDateTimeForRails(fullDate);
    }

    if (!options.silent) {
      this.displayTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
    requestAnimationFrame(() => this.toggleClearVisibility());
  }

  private extractDatePart(value: string): string | null {
    const m = value.match(/^(\w+ \d{1,2}, \d{4})/);
    return m ? m[1] : null;
  }

  private defaultHour(): string {
    return this.is24HourFormat()
      ? (this.constructor as any).DEFAULT_HOUR_24
      : (this.constructor as any).DEFAULT_HOUR_12;
  }

  private defaultMinute(): string {
    return (this.constructor as any).DEFAULT_MINUTE;
  }

  private defaultAmpm(): string {
    return (this.constructor as any).DEFAULT_AMPM;
  }

  buildTimeUI(): void {
    this.timeContainer = document.createElement('div');
    this.timeContainer.className = 'toolkit-date-picker__time';
    const is24h = this.is24HourFormat();

    this.hourSelect = document.createElement('select');
    this.hourSelect.className = 'toolkit-date-picker__time-select';
    this.hourSelect.setAttribute('aria-label', 'Hour');

    if (is24h) {
      for (let h = 0; h <= 23; h++) {
        const opt = document.createElement('option');
        const hh = String(h).padStart(2, '0');
        opt.value = hh;
        opt.textContent = hh;
        this.hourSelect.appendChild(opt);
      }
    } else {
      for (let h = 1; h <= 12; h++) {
        const opt = document.createElement('option');
        opt.value = String(h);
        opt.textContent = String(h);
        this.hourSelect.appendChild(opt);
      }
    }

    this.minuteSelect = document.createElement('select');
    this.minuteSelect.className = 'toolkit-date-picker__time-select';
    this.minuteSelect.setAttribute('aria-label', 'Minutes');

    (this.constructor as any).MINUTE_STEPS.forEach((m: number) => {
      const mm = String(m).padStart(2, '0');
      const opt = document.createElement('option');
      opt.value = mm;
      opt.textContent = mm;
      this.minuteSelect!.appendChild(opt);
    });

    const now = new Date();
    const rounded = this.roundToQuarter(now);
    const minuteBlock = String(rounded.getMinutes()).padStart(2, '0');

    if (is24h) {
      const hour24 = String(rounded.getHours()).padStart(2, '0');
      this.hourSelect.value = hour24;
    } else {
      const hour12 = this.toHour12(rounded.getHours());
      const ampm = rounded.getHours() >= 12 ? 'PM' : 'AM';
      this.hourSelect.value = hour12;

      this.ampmSelect = document.createElement('select');
      this.ampmSelect.className = 'toolkit-date-picker__time-select';
      this.ampmSelect.setAttribute('aria-label', 'AM or PM');

      ['AM', 'PM'].forEach(ap => {
        const opt = document.createElement('option');
        opt.value = ap;
        opt.textContent = ap;
        this.ampmSelect!.appendChild(opt);
      });

      this.ampmSelect.value = ampm;
    }

    this.minuteSelect.value = minuteBlock;

    this.timeContainer.appendChild(this.hourSelect);
    this.timeContainer.appendChild(document.createTextNode(':'));
    this.timeContainer.appendChild(this.minuteSelect);

    if (!is24h && this.ampmSelect) {
      this.timeContainer.appendChild(this.ampmSelect);
    }
  }

  private roundToQuarter(date: Date): Date {
    const minutes = date.getMinutes();
    const rounded = Math.round(minutes / 15) * 15;

    if (rounded === 60) {
      date.setHours(date.getHours() + 1);
      date.setMinutes(0);
    } else {
      date.setMinutes(rounded);
    }

    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  private toHour12(h24: number): string {
    let h = h24 % 12;
    if (h === 0) h = 12;
    return String(h);
  }

  private convertTo24Hour(hour12: number, ampm: string): number {
    if (ampm === 'PM' && hour12 !== 12) return hour12 + 12;
    if (ampm === 'AM' && hour12 === 12) return 0;
    return hour12;
  }

  // Format date for Rails without timezone information (YYYY-MM-DD HH:mm:ss)
  private formatDateTimeForRails(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  private picker(): any {
    if (typeof $ === 'undefined') return null;

    try {
      return ($(this.calendarTarget) as any).pickadate('picker');
    } catch {
      return null;
    }
  }

  private formatDate(date: Date): string {
    const dateFormat = this.element.getAttribute('data-date-format');

    if (!dateFormat) {
      const months = (this.constructor as any).MONTH_NAMES;
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    }
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const months = (this.constructor as any).MONTH_NAMES;
    const monthsAbbr = (this.constructor as any).MONTH_NAMES_ABBR;

    const monthName = months[date.getMonth()];
    const monthAbbr = monthsAbbr[date.getMonth()];

    const dd = String(day).padStart(2, '0');
    const mm = String(month).padStart(2, '0');
    const yyyy = String(year);

    return dateFormat.replace(/MMMM|MMM|YYYY|DD|MM|D/g, (match) => {
      switch (match) {
        case 'MMMM': return monthName;
        case 'MMM': return monthAbbr;
        case 'YYYY': return yyyy;
        case 'DD': return dd;
        case 'MM': return mm;
        case 'D': return String(day);
        default: return match;
      }
    });
  }

  private handleNaturalLanguagePreview(): void {
    if (!this.hasFullTarget) return;

    const dateInput = this.fullTarget;
    const tooltip = this.element.querySelector('.date-picker-hint') as HTMLElement;
    if (!tooltip) return;

    const tooltipText = tooltip.dataset.text || '';
    const inputValue = dateInput.value.trim();

    if (!inputValue) {
      tooltip.textContent = tooltipText;
      return;
    }

    let date = this.parseDateTimeString(inputValue);

    if (!date) {
      date = this.parseNaturalLanguage(inputValue);
    }

    const formattedParsed = date ? this.formatFullDateTime(date) : null;

    if (formattedParsed) {
      tooltip.textContent = formattedParsed;
      tooltip.classList.add('toolkit-date-picker__hint--preview');
    } else {
      tooltip.textContent = '...';
    }
  }

  private applyNaturalLanguageParsing(): void {
    if (!this.hasFullTarget) return;

    const dateInput = this.fullTarget;
    const tooltip = this.element.querySelector('.date-picker-hint') as HTMLElement;
    const currentValue = dateInput.value.trim();

    if (!currentValue) {
      this.restoreTooltipText();
      return;
    }

    if (this.isAlreadyFormatted(currentValue)) {
      const parsedDate = this.parseDateTimeString(currentValue);

      if (parsedDate && !isNaN(parsedDate.getTime())) {
        if (this.hasHiddenTarget) {
          this.hiddenTarget.value = this.formatDateTimeForRails(parsedDate);
        }
        this.syncTimeSelectsFromParsedDate(parsedDate);

        const picker = this.picker();
        if (picker) {
          picker.set('select', parsedDate, { muted: true });
        }
      }

      this.restoreTooltipText();
      return;
    }

    const parsedDate = this.parseNaturalLanguage(currentValue);

    if (parsedDate) {
      const formatted = this.formatFullDateTime(parsedDate);
      dateInput.value = formatted;

      if (this.hasHiddenTarget) {
        this.hiddenTarget.value = this.formatDateTimeForRails(parsedDate);
      }

      this.syncTimeSelectsFromParsedDate(parsedDate);

      const picker = this.picker();
      if (picker) {
        picker.set('select', parsedDate, { muted: true });
      }

      dateInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.restoreTooltipText();
  }

  private isAlreadyFormatted(value: string): boolean {
    if (!value) return false;

    const dateFormat = this.element.getAttribute('data-date-format');
    const is24h = this.is24HourFormat();
    let datePattern: string;

    if (!dateFormat || dateFormat === 'MMMM D, YYYY') {
      datePattern = '\\w+ \\d{1,2}, \\d{4}';
    } else if (dateFormat.includes('/')) {
      datePattern = '\\d{1,4}\\/\\d{1,2}\\/\\d{1,4}';
    } else if (dateFormat.includes('-')) {
      datePattern = '\\d{1,4}-\\d{1,2}-\\d{1,4}';
    } else if (dateFormat.includes('.')) {
      datePattern = '\\d{1,4}\\.\\d{1,2}\\.\\d{1,4}';
    } else {
      datePattern = '[\\w\\d\\/\\.\\-, ]+';
    }

    const timePattern = is24h
      ? '\\d{1,2}:\\d{2}'
      : '\\d{1,2}:\\d{2} (AM|PM)';

    const fullPattern = new RegExp(`^${datePattern} ${timePattern}$`);

    return fullPattern.test(value);
  }

  private parseNaturalLanguage(input: string): Date | null {
    if (typeof chrono === 'undefined') return null;

    try {
      const parsed = chrono.parseDate(input);
      return parsed ? this.roundToQuarter(parsed) : null;
    } catch (e) {
      console.warn('[date-picker] natural language parsing error', e);
      return null;
    }
  }

  private formatFullDateTime(date: Date): string {
    const datePart = this.formatDate(date);
    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (this.is24HourFormat()) {
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      return `${datePart} ${hh}:${mm}`;
    } else {
      const hour12 = this.toHour12(hours);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const mm = String(minutes).padStart(2, '0');
      return `${datePart} ${hour12}:${mm} ${ampm}`;
    }
  }

  private parseDateTimeString(dateTimeStr: string): Date | null {
    if (!dateTimeStr) return null;

    const dateFormat = this.element.getAttribute('data-date-format');
    const timeFormat = this.element.getAttribute('data-time-format');

    if (!dateFormat || !timeFormat) {
      const date = new Date(dateTimeStr);
      return isNaN(date.getTime()) ? null : date;
    }

    const formatParts = dateFormat.split(/[-\/\s,.]+/).filter(part => part.length > 0);
    let groupIndex = 1;
    const componentMap: { [key: number]: string } = {};

    const patternParts = formatParts.map(part => {
      if (part === 'DD' || part === 'D') {
        componentMap[groupIndex] = 'day';
        groupIndex++;
        return '(\\d{1,2})';
      } else if (part === 'MM' || part === 'M') {
        componentMap[groupIndex] = 'month_num';
        groupIndex++;
        return '(\\d{1,2})';
      } else if (part === 'MMMM') {
        componentMap[groupIndex] = 'month_name';
        groupIndex++;
        return '(\\w+)';
      } else if (part === 'MMM') {
        componentMap[groupIndex] = 'month_abbr';
        groupIndex++;
        return '(\\w+)';
      } else if (part === 'YYYY' || part === 'YY') {
        componentMap[groupIndex] = 'year';
        groupIndex++;
        return '(\\d{2,4})';
      }
      return part;
    });

    const flexibleSeparator = '[\\s,\\/.-]+';
    const datePattern = patternParts.join(flexibleSeparator);
    let timePattern: string;

    if (timeFormat === 'HH:mm') {
      timePattern = '(\\d{1,2}):(\\d{2})';
      componentMap[groupIndex] = 'hour';
      componentMap[groupIndex + 1] = 'minute';
      groupIndex += 2;
    } else {
      timePattern = '(\\d{1,2}):(\\d{2})\\s+(AM|PM)';
      componentMap[groupIndex] = 'hour';
      componentMap[groupIndex + 1] = 'minute';
      componentMap[groupIndex + 2] = 'ampm';
      groupIndex += 3;
    }

    const fullPattern = new RegExp(`^${datePattern}\\s+${timePattern}$`);

    const match = dateTimeStr.match(fullPattern);
    if (!match) return null;
    let day: number | undefined;
    let month: number | undefined;
    let year: number | undefined;
    let hour: number | undefined;
    let minute: number | undefined;
    let ampm: string | undefined;

    const months = (this.constructor as any).MONTH_NAMES;
    const monthsAbbr = (this.constructor as any).MONTH_NAMES_ABBR;

    for (let i = 1; i < match.length; i++) {
      const componentType = componentMap[i];
      const value = match[i];

      switch (componentType) {
        case 'day':
          day = parseInt(value, 10);
          break;
        case 'month_num':
          month = parseInt(value, 10) - 1;
          break;
        case 'month_name':
          month = months.indexOf(value);
          if (month === -1) month = undefined;
          break;
        case 'month_abbr':
          month = monthsAbbr.indexOf(value);
          if (month === -1) month = undefined;
          break;
        case 'year':
          year = parseInt(value, 10);
          if (year < 100) {
            year += year < 50 ? 2000 : 1900;
          }
          break;
        case 'hour':
          hour = parseInt(value, 10);
          break;
        case 'minute':
          minute = parseInt(value, 10);
          break;
        case 'ampm':
          ampm = value;
          break;
      }
    }

    if (day === undefined || month === undefined || year === undefined ||
        hour === undefined || minute === undefined) {
      return null;
    }
    if (ampm) {
      if (ampm === 'PM' && hour !== 12) hour += 12;
      if (ampm === 'AM' && hour === 12) hour = 0;
    }

    return new Date(year, month, day, hour, minute);
  }
}
